import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Space, Typography, Segmented, Collapse } from "antd";
import { BaseFilterItem } from "components/UI/BaseFilter/BaseFilterItem/BaseFilterItem.tsx";
import { useBaseFilter } from "components/UI/BaseFilter/hooks/useBaseFilter.ts";
import { FilterGroup as FilterGroupType } from "components/UI/BaseFilter/types/filter";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";

type OfferData = App.Data.OfferData;

interface FilterGroupProps {
    group: FilterGroupType;
    offer?: OfferData;
    cascade?: { id: string };
}

export const FilterGroup = ({ group, offer, cascade }: FilterGroupProps) => {
    const baseFilterUtils = useBaseFilter(offer);
    const availableFilterColumns = baseFilterUtils.getAvailableFilterColumns();
    const { t } = usePaldockTranslation("ui.offer-filter");

    const updateInternalLogicalOperator = (groupId: number, logicalOperator: "and" | "or") => {
        if (cascade) {
            baseFilterUtils.updateCascadeFilterGroup(cascade.id, groupId, "internalLogicalOperator", logicalOperator);
        } else if (offer) {
            baseFilterUtils.updateFilterGroup(offer, groupId, "internalLogicalOperator", logicalOperator);
        }
    };

    const removeGroup = () => {
        if (cascade) {
            baseFilterUtils.removeCascadeFilterGroup(cascade.id, group.id);
        } else if (offer) {
            baseFilterUtils.removeFilterGroup(offer, group.id);
        }
    };

    const addFilterToGroup = () => {
        if (cascade) {
            baseFilterUtils.addCascadeFilterToGroup(cascade.id, group.id);
        } else if (offer) {
            baseFilterUtils.addFilterToGroup(offer, group.id);
        }
    };

    const items = [
        {
            key: group.id.toString(),
            label: (
                <Row justify="space-between" align="middle" style={{ width: "100%" }}>
                    <Col>
                        <Typography.Title level={5} style={{ margin: 0 }}>
                            {group.name}
                        </Typography.Title>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                size="small"
                                type={"link"}
                                icon={<PlusOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    addFilterToGroup();
                                }}
                            >
                                {t("add_filter")}
                            </Button>
                            <Button
                                size="small"
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeGroup();
                                }}
                                aria-label="Remove filter group"
                            />
                        </Space>
                    </Col>
                </Row>
            ),
            children: (
                <div>
                    {group.filters.length > 0 ? (
                        <div>
                            {group.filters.map((filter, index) => (
                                <div key={filter.id}>
                                    <div className="mb-2">
                                        <BaseFilterItem
                                            filter={filter}
                                            offer={offer}
                                            cascade={cascade}
                                            availableFilterColumns={availableFilterColumns}
                                            groupId={group.id}
                                        />
                                    </div>
                                    {/* Show logical operator between filters within group (not after the last one) */}
                                    {index < group.filters.length - 1 && (
                                        <div className="flex justify-center my-4">
                                            <Segmented
                                                value={group.internalLogicalOperator}
                                                onChange={(value) =>
                                                    updateInternalLogicalOperator(group.id, value as "and" | "or")
                                                }
                                                options={[
                                                    { label: t("labels.and"), value: "and" },
                                                    { label: t("labels.or"), value: "or" },
                                                ]}
                                                style={{ width: "250px" }}
                                                className="[&_.ant-segmented-item]:!w-[125px]"
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-gray-500 py-4">
                            <Typography.Text type="secondary">{t("actions.click_to_add_filter")}</Typography.Text>
                        </div>
                    )}
                </div>
            ),
        },
    ];

    return (
        <Card className="rounded-lg w-full border-none bg-gray-200">
            <Collapse
                items={items}
                defaultActiveKey={[group.id.toString()]}
                ghost
                expandIconPosition="start"
                className="[&_.ant-collapse-header]:!p-0 [&_.ant-collapse-header]:!pb-4"
            />
        </Card>
    );
};
